import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { IconChoices } from '../../common/Icon'
import { Button } from '../../common/Button'
import { Notification } from '../Notification'
import { NotificationBadge } from '../NotificationBadge'
import { Dropdown } from '../../common/Dropdown'
import { notificationsQueries, setSeen } from '../../../api/notifications'
import { useAuth } from '../../../hooks/useAuth'

interface NotificationButtonControllerProps {
  navBarHeight?: number
  buttonSize?: 'sm' | 'md' | 'lg' | 'no-padding'
}

export const NotificationButtonController = ({
  navBarHeight,
  buttonSize,
}: NotificationButtonControllerProps): React.ReactNode => {
  const [showNotifications, setShowNotifications] = useState(false)
  const { userId } = useAuth()
  const { data: unseenUnReadCount, isLoading } = useQuery(
    notificationsQueries.getUserUnseenUnreadCount(userId)
  )
  const displayNotificationBadge =
    !!unseenUnReadCount?.unseen.notificationScreenUnseenCount
  const { data: sortedNotifications } = useQuery(notificationsQueries.list())

  const { mutate: mutateSetSeen } = useMutation({
    mutationFn: setSeen,
    onError: (error) => {
      captureException(error)
    },
  })

  useEffect(() => {
    if (!showNotifications) return

    const unseenNotifications = sortedNotifications?.filter(
      (notification) =>
        !/^chat.*message/.test(notification.type) &&
        notification.isSeen === false
    )
    if (unseenNotifications && unseenNotifications.length > 0) {
      mutateSetSeen({
        notificationIds: unseenNotifications.map(
          (notification) => notification._id
        ),
      })
    }
  }, [mutateSetSeen, showNotifications, sortedNotifications])

  const filteredNotifications = sortedNotifications?.filter(
    (notification) =>
      !/^chat.*message/.test(notification.type) &&
      notification.type !== 'user-added-to-unit' &&
      notification.type !== 'invite-all-admin-to-society'
  )

  const dropdownOptions =
    filteredNotifications?.map((notification) => ({
      value: notification._id,
      label: notification._id,
      customContent: (
        <Notification key={notification._id} notification={notification} />
      ),
    })) || []

  return (
    <Dropdown
      dropdownClassName="max-h-[400px] md:max-h-[600px] overflow-y-auto"
      style={{
        height: navBarHeight ? navBarHeight - 1 : navBarHeight,
        width: navBarHeight,
      }}
      dropdownPosition="bottom-left"
      dropdownContentWidth="w-fit"
      isLoading={isLoading}
      options={dropdownOptions}
      openChanged={(open) => setShowNotifications(open)}
    >
      <NotificationBadge
        show={displayNotificationBadge}
        className="h-full w-full"
      >
        <Button
          wrapperClassName="h-full w-full"
          icon={IconChoices.BELL}
          className="h-full w-full rounded-none border-none bg-transparent"
          size={buttonSize}
          disableTabIndex
        />
      </NotificationBadge>
    </Dropdown>
  )
}
